if (!customElements.get('newsletter-offer')) {
  class NewsletterOffer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[role="dialog"]');
      this.trigger = this.querySelector('[data-newsletter-offer-open]');
      this.closeButtons = this.querySelectorAll('[data-newsletter-offer-close]');
      this.serverOpen = this.querySelector('[data-newsletter-offer-server-open]');
      this.form = this.querySelector('form');
      this.formState = this.querySelector('[data-newsletter-offer-form-state]');
      this.successState = this.querySelector('[data-newsletter-offer-success]');
      this.fallbackState = this.querySelector('[data-newsletter-offer-fallback]');
      this.existingState = this.querySelector('[data-newsletter-offer-existing]');
      this.dismissKey = 'prior-era-newsletter-offer-dismissed-at';
      this.successKey = 'prior-era-newsletter-offer-subscribed-at';
      this.sessionKey = 'prior-era-newsletter-offer-dismissed-session';
      this.returnKey = 'prior-era-newsletter-offer-return-path';
      this.returnScrollKey = 'prior-era-newsletter-offer-return-scroll';
      this.subscriptionPendingKey = 'prior-era-newsletter-offer-subscription-pending';
      this.discountReturnKey = 'prior-era-newsletter-offer-discount-return';
      this.discountCode = this.dataset.discountCode;
      this.subscriptionSucceeded =
        this.querySelector('[data-newsletter-subscription-result]')?.dataset.success === 'true';
      this.autoOpen = this.dataset.autoOpen === 'true';
      this.delay = Number(this.dataset.delay) || 4000;
      this.dismissDuration = (Number(this.dataset.dismissDays) || 7) * 86400000;
      this.successDuration = (Number(this.dataset.successDays) || 30) * 86400000;
      this.previouslyFocused = null;
      this.retryCount = 0;
      this.submitLabel = this.form?.querySelector('button[type="submit"]')?.textContent.trim();

      this.hidden = false;
      this.trigger?.addEventListener('click', () => this.open(this.trigger));
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(true)));
      this.form?.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.syncStickyCartOffset = this.syncStickyCartOffset.bind(this);
      this.watchStickyCart();

      const discountReturn = this.consumeDiscountReturn();
      if (discountReturn === 'applied') {
        this.showResultState(this.successState);
        return;
      }
      if (discountReturn === 'failed') {
        this.showResultState(this.fallbackState);
        return;
      }

      if (this.hasConfirmedSubscriptionReturn()) {
        this.applyDiscount();
        return;
      }

      if (this.serverOpen) {
        this.open(null, true);
        return;
      }

      if (this.timestampIsCurrent(this.successKey, this.successDuration)) {
        this.hideTrigger();
        return;
      }

      if (!this.autoOpen) {
        this.showTrigger();
        return;
      }

      const dismissedThisSession = this.readSession(this.sessionKey);
      const recentlyDismissed = this.timestampIsCurrent(this.dismissKey, this.dismissDuration);

      if (dismissedThisSession || recentlyDismissed) {
        this.showTrigger();
        return;
      }

      window.setTimeout(() => this.tryAutomaticOpen(), this.delay);
    }

    disconnectedCallback() {
      this.stickyCartObserver?.disconnect();
      window.removeEventListener('resize', this.syncStickyCartOffset);
    }

    onSubmit(event) {
      if (event.defaultPrevented || !this.form?.checkValidity()) return;
      if (this.submitting) {
        event.preventDefault();
        return;
      }

      this.submitting = true;

      const returnPath = `${window.location.pathname}${window.location.search}`;
      this.writeSessionValue(this.returnKey, returnPath);
      this.writeSessionValue(this.returnScrollKey, String(Math.max(0, Math.round(window.scrollY))));
      this.writeSessionValue(this.subscriptionPendingKey, 'true');

      const returnInput = this.form.querySelector('input[name="return_to"]');
      if (returnInput) {
        const returnUrl = new URL(returnPath, window.location.origin);
        returnUrl.searchParams.set('pe_newsletter_subscribed', '1');
        returnInput.value = `${returnUrl.pathname}${returnUrl.search}#PriorEraNewsletterOffer`;
      }

      const submitButton = this.form.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.textContent = 'PROCESSING…';
      }

      window.clearTimeout(this.processingTimer);
      this.processingTimer = window.setTimeout(() => this.resetSubmitButton(), 15000);
    }

    resetSubmitButton() {
      const submitButton = this.form?.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.removeAttribute('aria-disabled');
        if (this.submitLabel) submitButton.textContent = this.submitLabel;
      }
      this.submitting = false;
    }

    hasConfirmedSubscriptionReturn() {
      const url = new URL(window.location.href);
      const hasReturnMarker = url.searchParams.get('pe_newsletter_subscribed') === '1';
      const submissionWasPending = this.readSessionValue(this.subscriptionPendingKey) === 'true';

      if (!this.subscriptionSucceeded && !(hasReturnMarker && submissionWasPending)) return false;

      this.removeSessionValue(this.subscriptionPendingKey);
      if (hasReturnMarker) {
        url.searchParams.delete('pe_newsletter_subscribed');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
      return true;
    }

    applyDiscount() {
      if (!this.discountCode) {
        this.showResultState(this.fallbackState);
        return;
      }

      const returnPath = this.readSessionValue(this.returnKey) || `${window.location.pathname}${window.location.search}`;
      const safeReturnPath = returnPath.startsWith('/') && !returnPath.startsWith('//') ? returnPath : '/';
      const discountReturnUrl = new URL(safeReturnPath, window.location.origin);
      this.writeSessionValue(this.discountReturnKey, 'true');
      try {
        window.location.assign(
          `/discount/${encodeURIComponent(this.discountCode)}?redirect=${encodeURIComponent(discountReturnUrl.pathname)}`
        );
      } catch (error) {
        this.removeSessionValue(this.discountReturnKey);
        this.showResultState(this.fallbackState);
      }
    }

    consumeDiscountReturn() {
      const discountWasPending = this.readSessionValue(this.discountReturnKey) === 'true';
      if (!discountWasPending) return false;

      const savedReturnPath = this.readSessionValue(this.returnKey) || '/';
      const expectedReturnUrl = new URL(savedReturnPath, window.location.origin);
      const returnedThroughShopify = window.location.pathname === expectedReturnUrl.pathname;

      this.removeSessionValue(this.discountReturnKey);
      const savedScroll = Number(this.readSessionValue(this.returnScrollKey));
      this.removeSessionValue(this.returnKey);
      this.removeSessionValue(this.returnScrollKey);

      if (returnedThroughShopify)
        window.history.replaceState(
          {},
          '',
          `${expectedReturnUrl.pathname}${expectedReturnUrl.search}${expectedReturnUrl.hash}`
        );

      if (Number.isFinite(savedScroll) && savedScroll > 0) {
        window.requestAnimationFrame(() => window.scrollTo({ top: savedScroll, behavior: 'auto' }));
      }

      return returnedThroughShopify ? 'applied' : 'failed';
    }

    showResultState(state) {
      this.resultShown = true;
      this.formState?.setAttribute('hidden', '');
      this.existingState?.setAttribute('hidden', '');
      this.successState?.setAttribute('hidden', '');
      this.fallbackState?.setAttribute('hidden', '');
      state?.removeAttribute('hidden');
      state?.classList.add('is-active');
      this.storeTimestamp(this.successKey);
      this.open(null, true);
      this.hideTrigger();
    }

    watchStickyCart() {
      this.stickyCart = document.querySelector('prior-era-sticky-cart');
      if (!this.stickyCart) return;

      this.stickyCartObserver = new MutationObserver(this.syncStickyCartOffset);
      this.stickyCartObserver.observe(this.stickyCart, { attributes: true, attributeFilter: ['hidden'] });
      window.addEventListener('resize', this.syncStickyCartOffset);
      this.syncStickyCartOffset();
    }

    syncStickyCartOffset() {
      const stickyCartVisible =
        this.stickyCart &&
        !this.stickyCart.hidden &&
        window.matchMedia('(max-width: 989px)').matches;

      if (!stickyCartVisible) {
        this.style.removeProperty('--pe-newsletter-trigger-bottom');
        return;
      }

      const safeGap = window.matchMedia('(max-width: 749px)').matches ? 14 : 24;
      this.style.setProperty(
        '--pe-newsletter-trigger-bottom',
        `${Math.ceil(this.stickyCart.getBoundingClientRect().height + safeGap)}px`
      );
    }

    tryAutomaticOpen() {
      if (this.hasBlockingInterface()) {
        this.retryCount += 1;
        if (this.retryCount <= 8) window.setTimeout(() => this.tryAutomaticOpen(), 2000);
        else this.showTrigger();
        return;
      }

      this.open(null);
    }

    hasBlockingInterface() {
      const privacyInterface = Array.from(
        document.querySelectorAll(
          '#shopify-pc__banner, #shopify-pc__prefs, .shopify-pc__banner__dialog, [data-shopify-privacy-banner]'
        )
      ).some((element) => element.getClientRects().length > 0);

      return (
        privacyInterface ||
        document.body.classList.contains('overflow-hidden') ||
        document.body.classList.contains('overflow-hidden-mobile') ||
        document.body.classList.contains('overflow-hidden-tablet') ||
        Boolean(document.querySelector('cart-drawer.active, details[open] > .menu-drawer'))
      );
    }

    open(opener = null, force = false) {
      if (!force && this.timestampIsCurrent(this.successKey, this.successDuration)) return;
      this.previouslyFocused = opener || document.activeElement;
      this.classList.add('is-open');
      this.trigger?.setAttribute('hidden', '');
      document.body.classList.add('pe-newsletter-offer-open');
      this.dialog?.focus({ preventScroll: true });
      if (typeof trapFocus === 'function' && this.dialog) {
        const focusTarget =
          this.dialog.querySelector('input:not([type="hidden"]):not([hidden]), button:not([hidden])') || this.dialog;
        trapFocus(this.dialog, focusTarget);
      }
    }

    close(recordDismissal = false) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('pe-newsletter-offer-open');

      if (this.resultShown) {
        this.hideTrigger();
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.previouslyFocused);
        else this.previouslyFocused?.focus?.();
        return;
      }

      if (recordDismissal) {
        this.storeTimestamp(this.dismissKey);
        this.writeSession(this.sessionKey);
      }
      this.showTrigger();
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.previouslyFocused);
      else this.previouslyFocused?.focus?.();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        event.preventDefault();
        this.close(true);
      }
    }

    showTrigger() {
      if (this.trigger && !this.timestampIsCurrent(this.successKey, this.successDuration)) {
        this.trigger.removeAttribute('hidden');
      }
    }

    hideTrigger() {
      this.trigger?.setAttribute('hidden', '');
    }

    timestampIsCurrent(key, duration) {
      try {
        const timestamp = Number(window.localStorage.getItem(key));
        return timestamp > 0 && Date.now() - timestamp < duration;
      } catch (error) {
        return false;
      }
    }

    storeTimestamp(key) {
      try {
        window.localStorage.setItem(key, String(Date.now()));
      } catch (error) {
        // Storage can be unavailable in private browsing; the form still works.
      }
    }

    readSession(key) {
      try {
        return window.sessionStorage.getItem(key) === 'true';
      } catch (error) {
        return false;
      }
    }

    writeSession(key) {
      try {
        window.sessionStorage.setItem(key, 'true');
      } catch (error) {
        // Session suppression is an enhancement, not a subscription control.
      }
    }

    readSessionValue(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    writeSessionValue(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (error) {
        // Session state only preserves the shopping location during the native redirect.
      }
    }

    removeSessionValue(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        // Storage can be unavailable; the discount route still applies the offer.
      }
    }
  }

  customElements.define('newsletter-offer', NewsletterOffer);
}
